import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Box, Paper, Typography, Card, CardContent,
  CircularProgress, Alert, Chip, FormControl,
  InputLabel, Select, MenuItem, Slider
} from '@mui/material';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import NetworkGraph, { NetworkNode, NetworkLink } from './NetworkGraph';

type Goal = {
  id: number;
  member_id: number;
  type: 'professional' | 'personal';
  title: string;
  description: string;
  skills_needed: string;
  member_name: string;
  member_role: string;
  priority: number;
};

type SemanticAlignment = {
  goal1: { id: number; title: string; member: string };
  goal2: { id: number; title: string; member: string };
  similarity: number;
};

type GoalNode = NetworkNode & {
  type: 'goal' | 'member';
  title: string;
  member?: string;
  role?: string;
  goalType?: 'professional' | 'personal';
  priority?: number;
  skills?: string[];
};

type GoalLink = NetworkLink & {
  similarity?: number;
};

export default function GoalMapping() {
  const { currentTeam } = useAuth();
  const isAdmin = currentTeam?.role === 'admin' || currentTeam?.role === 'manager';

  const [allGoals, setAllGoals] = useState<Goal[]>([]);
  const [allPairs, setAllPairs] = useState<SemanticAlignment[]>([]);
  const [userMemberId, setUserMemberId] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<'all' | 'professional' | 'personal'>('all');
  const [minSim, setMinSim] = useState(0.70);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      const similarityRes = await axios.get(apiUrl('/api/goals/analysis/similarity'));
      // The similarity endpoint returns { pairs: [...], goals: [...], userMemberId: number }
      const data = similarityRes.data || {};
      const pairsData = Array.isArray(data.pairs) ? data.pairs : [];
      const goalsData = Array.isArray(data.goals) ? data.goals : [];
      const memberId = data.userMemberId;

      // Store raw data - filtering will happen in useMemo based on slider
      setAllPairs(pairsData.filter((p: SemanticAlignment) => 
        typeof p?.similarity === 'number' && Number.isFinite(p.similarity)
      ));
      setAllGoals(goalsData);
      setUserMemberId(memberId);
    } catch (e) {
      console.error(e);
      setError('Failed to load goal mapping data');
    } finally {
      setLoading(false);
    }
  }

  // Derived state based on slider and filter
  const { filteredGoals, topPairs, stats, networkNodes, networkLinks } = useMemo(() => {
    // Filter pairs based on slider threshold (>= to include threshold value)
    const filteredPairs = allPairs.filter(p => (p.similarity ?? 0) >= minSim);

    let relevantGoals: Goal[];

    if (isAdmin) {
      // For admins: show all goals
      relevantGoals = allGoals;
    } else {
      // For regular users: show their own goals + goals that appear in pairs with their goals
      const userGoalIds = new Set(
        allGoals
          .filter((goal: Goal) => goal.member_id === userMemberId)
          .map(g => g.id)
      );

      // Find pairs that involve user's goals
      const pairsWithUserGoals = filteredPairs.filter(p => 
        userGoalIds.has(p.goal1.id) || userGoalIds.has(p.goal2.id)
      );

      // Collect all goal IDs from these pairs
      const goalIdsInPairs = new Set<number>();
      pairsWithUserGoals.forEach((p: SemanticAlignment) => {
        goalIdsInPairs.add(p.goal1.id);
        goalIdsInPairs.add(p.goal2.id);
      });

      // Include user's own goals + goals that appear in pairs with user's goals
      relevantGoals = allGoals.filter((goal: Goal) => 
        goalIdsInPairs.has(goal.id)
      );
    }

    // Apply type filter
    const g = filterType === 'all' 
      ? relevantGoals 
      : relevantGoals.filter(x => x.type === filterType);

    // Filter pairs to only include those whose goals exist in current filter
    const inFilter = new Set(g.map(x => x.id));
    const passing = filteredPairs
      .filter(p => inFilter.has(p.goal1.id) && inFilter.has(p.goal2.id))
      .sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));

    const top = passing.slice(0, 10);

    // Build network nodes - only include members who have goals in the filtered set
    const goalNodes: GoalNode[] = g.map(goal => ({
      id: `goal-${goal.id}`,
      type: 'goal',
      title: goal.title,
      member: goal.member_name,
      role: goal.member_role,
      goalType: goal.type,
      priority: goal.priority,
      skills: (goal.skills_needed || '').split(',').map(s => s.trim()).filter(Boolean)
    }));

    // Only include members who have goals in the filtered set
    const membersMap = new Map<string, { name: string; role?: string }>();
    g.forEach(goal => membersMap.set(goal.member_name, { name: goal.member_name, role: goal.member_role }));
    const memberNodes: GoalNode[] = Array.from(membersMap.values()).map(m => ({
      id: `member-${m.name}`,
      type: 'member',
      title: m.name,
      role: m.role
    }));

    const nodes: GoalNode[] = [...goalNodes, ...memberNodes];

    // Build network links
    const memberLinks: GoalLink[] = goalNodes.map(n => ({
      source: n.id,
      target: `member-${n.member}`,
    }));

    const semanticLinks: GoalLink[] = passing.map(p => ({
      source: `goal-${p.goal1.id}`,
      target: `goal-${p.goal2.id}`,
      similarity: Number(p.similarity) || 0
    }));

    const links: GoalLink[] = [...memberLinks, ...semanticLinks];

    return {
      filteredGoals: g,
      topPairs: top,
      stats: {
        totalGoals: g.length,
        totalPairs: passing.length,
      },
      networkNodes: nodes,
      networkLinks: links,
    };
  }, [allGoals, allPairs, userMemberId, minSim, filterType, isAdmin]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Goal Mapping Visualization</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box sx={{ width: 240 }}>
            <Typography variant="caption">Min similarity: {minSim.toFixed(2)}</Typography>
            <Slider
              size="small"
              step={0.05}
              min={0.5}
              max={0.95}
              value={minSim}
              onChange={(_, v) => setMinSim(v as number)}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter Goals</InputLabel>
            <Select
              label="Filter Goals"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
            >
              <MenuItem value="all">All Goals</MenuItem>
              <MenuItem value="professional">Professional Only</MenuItem>
              <MenuItem value="personal">Personal Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {filteredGoals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No goals available for mapping</Typography>
          <Typography variant="body2" color="text.secondary">Add some goals to see the network or change the similarity threshold.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Network */}
          <Paper sx={{ flex: 1, p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196f3' }} />
              <Typography variant="caption">Team Members</Typography>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
              <Typography variant="caption">Professional Goals</Typography>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
              <Typography variant="caption">Personal Goals</Typography>
              <Box sx={{ width: 20, height: 2, bgcolor: '#e53935' }} />
              <Typography variant="caption">Semantic Similarity</Typography>
            </Box>
            <Box sx={{ height: 600 }}>
              <NetworkGraph
                nodes={networkNodes}
                links={networkLinks}
                height={600}
                nodeRadius={(d) => (d as GoalNode).type === 'member' ? 20 : 15}
                nodeColor={(d) => {
                  const node = d as GoalNode;
                  return node.type === 'member' ? '#2196f3' : (node.goalType === 'professional' ? '#4caf50' : '#ff9800');
                }}
                nodeLabel={(d) => (d as GoalNode).title}
                nodeTooltip={(d) => {
                  const node = d as GoalNode;
                  return node.type === 'member'
                    ? `${node.title}\n${node.role || ''}`
                    : `${node.title}\nOwner: ${node.member}\nType: ${node.goalType}\nPriority: ${node.priority}/5\nSkills: ${node.skills?.join(', ') || 'None'}`;
                }}
                linkStroke={(d) => {
                  const sim = Number((d as GoalLink).similarity) || 0;
                  return sim >= 0.85 ? '#e53935' : sim > 0 ? '#9e9e9e' : '#bbb';
                }}
                linkStrokeWidth={(d) => {
                  const sim = Number((d as GoalLink).similarity) || 0;
                  return sim ? Math.max(1, (sim - 0.5) * 6) : 1.2;
                }}
                linkStrokeOpacity={(d) => {
                  const sim = Number((d as GoalLink).similarity) || 0;
                  return sim ? Math.min(0.9, 0.45 + (sim - 0.5) * 0.8) : 0.25;
                }}
                linkCurved={true}
                linkDistance={(d) => (d as GoalLink).similarity ? 80 : 100}
                forceStrength={{
                  charge: -300,
                  link: 0.8,
                  collision: 28,
                }}
              />
            </Box>
          </Paper>

          {/* Right Panel */}
          <Box sx={{ width: 340, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Summary</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Goals (filtered):</Typography>
                  <Chip size="small" label={stats.totalGoals} color="primary" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2">Semantic pairs ≥ {minSim.toFixed(2)}:</Typography>
                  <Chip size="small" label={stats.totalPairs} color="secondary" />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Top Semantic Matches</Typography>
                {topPairs.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No pairs above threshold.</Typography>
                )}
                {topPairs.map((p, idx) => {
                  const sim = Number(p.similarity) || 0;
                  return (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.25 }}>
                      <Typography variant="body2" sx={{ pr: 1 }}>
                        {p.goal1.member}: “{p.goal1.title}” ↔ {p.goal2.member}: “{p.goal2.title}”
                      </Typography>
                      <Chip label={sim.toFixed(2)} size="small" />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Container>
  );
}